import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { DashboardLayout } from "@/components/dashboard/DashboardLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { useNavigate } from "react-router-dom";
import {
  DollarSign,
  TrendingUp,
  Users,
  AlertTriangle,
  ArrowRight,
  PieChart,
  BarChart3,
  CheckCircle,
} from "lucide-react";

export default function CAPSManagement() {
  const navigate = useNavigate();

  const { data: pools } = useQuery({
    queryKey: ["revenue_sharing_pools"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("revenue_sharing_pools" as any)
        .select("*");
      if (error) throw error;
      return data;
    },
  });

  const { data: compensations } = useQuery({
    queryKey: ["athlete_compensation"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("athlete_compensation" as any)
        .select("*");
      if (error) throw error;
      return data;
    },
  });

  const { data: titleIXData } = useQuery({
    queryKey: ["title_ix_compliance"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("title_ix_compliance" as any)
        .select("*")
        .order("academic_year", { ascending: false })
        .limit(1)
        .single();
      if (error) throw error;
      return data;
    },
  });

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
    }).format(value);
  };

  const totalPoolValue =
    pools?.reduce((sum: number, pool: any) => sum + Number(pool.total_pool_amount || 0), 0) || 0;
  const totalAllocated =
    pools?.reduce((sum: number, pool: any) => sum + Number(pool.allocated_amount || 0), 0) || 0;
  const totalRemaining = totalPoolValue - totalAllocated;
  const allocationPercentage = totalPoolValue > 0 ? (totalAllocated / totalPoolValue) * 100 : 0;

  const pendingCompensations =
    compensations?.filter((c: any) => c.status === "pending").length || 0;
  const approvedCompensations =
    compensations?.filter((c: any) => c.status === "approved").length || 0;

  const activePools = pools?.filter((p: any) => p.status === "active").length || 0;

  const maleRevenue = Number((titleIXData as any)?.male_revenue_sharing || 0);
  const femaleRevenue = Number((titleIXData as any)?.female_revenue_sharing || 0);
  const totalRevenue = maleRevenue + femaleRevenue;
  const femalePercentage = totalRevenue > 0 ? (femaleRevenue / totalRevenue) * 100 : 0;

  return (
    <DashboardLayout>
      <div className="space-y-8">
        <div>
          <h1 className="text-4xl font-bold tracking-tight">CAPS Management</h1>
          <p className="text-muted-foreground mt-2">
            Comprehensive athlete payment system oversight and Title IX compliance
          </p>
        </div>

        {/* Key Metrics Overview */}
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">Total Pool Value</CardTitle>
              <DollarSign className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{formatCurrency(totalPoolValue)}</div>
              <p className="text-xs text-muted-foreground mt-1">
                Across {activePools} active pools
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">Allocated Funds</CardTitle>
              <TrendingUp className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{formatCurrency(totalAllocated)}</div>
              <p className="text-xs text-muted-foreground mt-1">
                {allocationPercentage.toFixed(1)}% of total pool
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">Pending Approvals</CardTitle>
              <Users className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{pendingCompensations}</div>
              <p className="text-xs text-muted-foreground mt-1">
                {approvedCompensations} approved this period
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">Title IX Status</CardTitle>
              {(titleIXData as any)?.compliance_status === "compliant" ? (
                <CheckCircle className="h-4 w-4 text-green-600" />
              ) : (
                <AlertTriangle className="h-4 w-4 text-yellow-600" />
              )}
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold capitalize">
                {(titleIXData as any)?.compliance_status || "N/A"}
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                {femalePercentage.toFixed(1)}% female allocation
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Pool Allocation Progress */}
        <Card>
          <CardHeader>
            <CardTitle>Pool Allocation Status</CardTitle>
            <CardDescription>Overall fund distribution across all pools</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <div className="flex items-center justify-between text-sm mb-2">
                <span className="text-muted-foreground">Total Allocated</span>
                <span className="font-medium">
                  {formatCurrency(totalAllocated)} / {formatCurrency(totalPoolValue)}
                </span>
              </div>
              <Progress value={allocationPercentage} className="h-3" />
            </div>
            <div className="grid grid-cols-2 gap-4 pt-4 border-t">
              <div>
                <p className="text-sm text-muted-foreground">Remaining Funds</p>
                <p className="text-2xl font-bold">{formatCurrency(totalRemaining)}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Utilization Rate</p>
                <p className="text-2xl font-bold">{allocationPercentage.toFixed(1)}%</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Quick Actions */}
        <div className="grid gap-4 md:grid-cols-3">
          <Card className="hover:shadow-lg transition-shadow cursor-pointer" onClick={() => navigate("/dashboard/caps/pools")}>
            <CardHeader>
              <div className="flex items-center justify-between">
                <PieChart className="h-8 w-8 text-primary" />
                <ArrowRight className="h-5 w-5 text-muted-foreground" />
              </div>
              <CardTitle className="mt-4">Revenue Sharing Pools</CardTitle>
              <CardDescription>
                Manage pools by sport and gender. Create new pools and monitor allocation.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button variant="outline" className="w-full">
                Manage Pools
              </Button>
            </CardContent>
          </Card>

          <Card className="hover:shadow-lg transition-shadow cursor-pointer" onClick={() => navigate("/dashboard/caps/compensation")}>
            <CardHeader>
              <div className="flex items-center justify-between">
                <DollarSign className="h-8 w-8 text-primary" />
                <ArrowRight className="h-5 w-5 text-muted-foreground" />
              </div>
              <CardTitle className="mt-4">Athlete Compensation</CardTitle>
              <CardDescription>
                Allocate funds to athletes, approve payments, and track distributions.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button variant="outline" className="w-full">
                Manage Compensation
              </Button>
            </CardContent>
          </Card>

          <Card className="hover:shadow-lg transition-shadow cursor-pointer" onClick={() => navigate("/dashboard/caps/title-ix")}>
            <CardHeader>
              <div className="flex items-center justify-between">
                <BarChart3 className="h-8 w-8 text-primary" />
                <ArrowRight className="h-5 w-5 text-muted-foreground" />
              </div>
              <CardTitle className="mt-4">Title IX Compliance</CardTitle>
              <CardDescription>
                Monitor compliance metrics and ensure equitable distribution of funds.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button variant="outline" className="w-full">
                View Compliance
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Recent Activity by Pool */}
        <Card>
          <CardHeader>
            <CardTitle>Active Pools Overview</CardTitle>
            <CardDescription>Current status of all active revenue sharing pools</CardDescription>
          </CardHeader>
          <CardContent>
            {pools && pools.length > 0 ? (
              <div className="space-y-4">
                {pools
                  .filter((p: any) => p.status === "active")
                  .slice(0, 5)
                  .map((pool: any) => {
                    const utilization =
                      pool.total_pool_amount > 0
                        ? (pool.allocated_amount / pool.total_pool_amount) * 100
                        : 0;
                    return (
                      <div key={pool.id} className="flex items-center gap-4 p-4 border rounded-lg">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <p className="font-medium">
                              {pool.sport} - {pool.gender}
                            </p>
                            <Badge variant="outline" className="text-xs">
                              {pool.academic_year}
                            </Badge>
                          </div>
                          <div className="flex items-center gap-4 text-sm text-muted-foreground">
                            <span>Total: {formatCurrency(pool.total_pool_amount)}</span>
                            <span>Allocated: {formatCurrency(pool.allocated_amount)}</span>
                            <span>Remaining: {formatCurrency(pool.total_pool_amount - pool.allocated_amount)}</span>
                          </div>
                        </div>
                        <div className="w-32">
                          <div className="text-right text-sm font-medium mb-1">
                            {utilization.toFixed(0)}%
                          </div>
                          <Progress value={utilization} className="h-2" />
                        </div>
                      </div>
                    );
                  })}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                No active pools found. Create a pool to get started.
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
